import { graphql } from 'gatsby';
import React, { FC, lazy } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco, nord } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  Value,
} from '@/components';
import { RefreshIcon } from '@/components/icons';
import { useTheme, useTranslate } from '@/contexts';
import formatJsonPayload from '@/utils/format-json-payload';
import formatDate from '@/utils/formatter/formatDate';
import formatMoney from '@/utils/formatter/formatMoney';
import useTransaction from './useTransaction';

const ZimplerWithdrawalManualApproveButton = lazy(
  () => import('./ZimplerWithdrawalManualApproveButton'),
);

export const Fragment = graphql`
  fragment SanityTransactionInspectionBlock on SanityTransactionInspectionBlock {
    title {
      ...SanityLocaleString
    }
  }
`;

const TransactionInspectionBlock: FC<{
  block: Queries.SanityTransactionInspectionBlockFragment;
}> = ({ block }) => {
  const { transaction, fetching, refresh } = useTransaction();
  const { t } = useTranslate();
  const theme = useTheme();

  const { code, language: lang } = formatJsonPayload(transaction?.payload);

  const syntaxStyle = theme.name === 'dark' ? nord : docco;

  const game =
    transaction?.gameDescriptor &&
    'json' in transaction?.gameDescriptor &&
    transaction.gameDescriptor.json;

  return (
    <Card
      size="lg"
      title={t(block.title)}
      options={
        <CardOptions>
          <CardOptionsButton
            className="flex"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            <Value
              fetching={fetching}
              title="Game Provider"
              value={transaction?.gameProvider}
            />
            <Value
              fetching={fetching}
              title="Game Studio"
              value={game?.gameProvider.gameProviderId}
            />
            <Value
              fetching={fetching}
              title="Processed At"
              value={formatDate(transaction?.processedAt ?? undefined)}
            />
            <Value fetching={fetching} title="Game Name" value={game?.name} />
            <Value
              fetching={fetching}
              title="Transaction Type"
              value={transaction?.transactionType}
            />
            <Value
              fetching={fetching}
              title="Transaction ID"
              value={transaction?.rawTransactionId}
            />
            <Value
              fetching={fetching}
              title="Ex. Transaction ID"
              value={transaction?.extTransactionId}
            />
            <Value
              fetching={fetching}
              title="Game ID"
              value={transaction?.gameId}
            />
            <Value
              fetching={fetching}
              title="Game Round ID"
              value={transaction?.gameRoundId}
            />
            <Value
              fetching={fetching}
              title="Game Session ID"
              value={transaction?.gameSessionId}
            />
            <Value
              fetching={fetching}
              title="Payment Method"
              value={transaction?.paymentMethod}
            />
            <Value
              fetching={fetching}
              title="Payment Status"
              value={transaction?.paymentStatus}
              suffix={
                transaction && (
                  <div className="mx-1">
                    <ZimplerWithdrawalManualApproveButton
                      transaction={transaction}
                    />
                  </div>
                )
              }
            />
            <Value
              fetching={fetching}
              title="Amount"
              value={formatMoney(transaction?.amount, transaction?.currency)}
            />
            <Value
              fetching={fetching}
              title="Opening Balance"
              value={formatMoney(
                transaction?.openingBalance,
                transaction?.currency,
              )}
            />
            <Value
              fetching={fetching}
              title="Closing Balance"
              value={formatMoney(
                transaction?.closingBalance,
                transaction?.currency,
              )}
            />
            <Value
              fetching={fetching}
              title="Payment Fee"
              value={formatMoney(
                transaction?.paymentFee?.money.amount,
                transaction?.paymentFee?.money.currency,
              )}
            />
            <Value
              fetching={fetching}
              title="Comment"
              value={transaction?.comment}
            />
          </div>
          <SyntaxHighlighter
            language={lang}
            style={syntaxStyle}
            className="col-span-full text-sm"
          >
            {code}
          </SyntaxHighlighter>
        </div>
      </CardBody>
    </Card>
  );
};

export default TransactionInspectionBlock;
